import { Trash2, Plus } from 'lucide-react';
import type {
  RuleCondition,
  LeafCondition,
  ConditionMetric,
  ConditionOp,
} from '../../services/sensorRules';

interface Props {
  value: RuleCondition;
  onChange: (next: RuleCondition) => void;
  availableMetrics: Array<{ key: ConditionMetric; label: string; unit?: string; disabled?: boolean; reason?: string }>;
  label?: string;
}

const OPS: Array<{ value: ConditionOp; label: string }> = [
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '≠' },
];

/**
 * Flat compound condition editor. Value is normalised to
 * { all: [...leaves] } or { any: [...leaves] }. A single leaf input is
 * promoted to { all: [leaf] } internally so the UI is uniform.
 */
export default function RuleConditionBuilder({ value, onChange, availableMetrics, label }: Props) {
  // Normalise into { combinator, leaves }
  const combinator: 'all' | 'any' =
    value && typeof value === 'object' && 'any' in value ? 'any' : 'all';
  const leaves: LeafCondition[] =
    value && typeof value === 'object' && 'all' in value
      ? (value.all as LeafCondition[])
      : value && typeof value === 'object' && 'any' in value
        ? (value.any as LeafCondition[])
        : [value as LeafCondition];

  function emit(next: LeafCondition[], newCombinator?: 'all' | 'any') {
    const c = newCombinator || combinator;
    onChange(c === 'all' ? { all: next } : { any: next });
  }

  function updateLeaf(idx: number, patch: Partial<LeafCondition>) {
    const next = leaves.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    emit(next);
  }

  function addLeaf() {
    const defaultMetric = availableMetrics.find(m => !m.disabled)?.key || 'value';
    emit([...leaves, { metric: defaultMetric, op: 'lt', value: 0 }]);
  }

  function removeLeaf(idx: number) {
    const next = leaves.filter((_, i) => i !== idx);
    emit(next.length === 0 ? [{ metric: 'value', op: 'lt', value: 0 }] : next);
  }

  return (
    <div className="space-y-2">
      {label && <div className="text-xs font-medium text-gray-600">{label}</div>}
      <div className="flex items-center gap-2 text-sm">
        <span>Trigger when</span>
        <select
          value={combinator}
          onChange={(e) => emit(leaves, e.target.value as 'all' | 'any')}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          <option value="all">all</option>
          <option value="any">any</option>
        </select>
        <span>of the following are true:</span>
      </div>

      <div className="space-y-2">
        {leaves.map((leaf, idx) => {
          const metric = availableMetrics.find(m => m.key === leaf.metric);
          return (
            <div
              key={idx}
              className="flex items-center gap-2 flex-wrap bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5"
            >
              <select
                value={leaf.metric}
                onChange={(e) => updateLeaf(idx, { metric: e.target.value as ConditionMetric })}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {availableMetrics.map(m => (
                  <option key={m.key} value={m.key} disabled={m.disabled} title={m.reason}>
                    {m.label}{m.unit ? ` (${m.unit})` : ''}{m.disabled ? ' — n/a' : ''}
                  </option>
                ))}
              </select>
              <select
                value={leaf.op}
                onChange={(e) => updateLeaf(idx, { op: e.target.value as ConditionOp })}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {OPS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="number"
                step="any"
                value={leaf.value}
                onChange={(e) => updateLeaf(idx, { value: Number(e.target.value) })}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-28"
              />
              {metric?.unit && (
                <span className="text-xs text-gray-500">{metric.unit}</span>
              )}
              <button
                type="button"
                onClick={() => removeLeaf(idx)}
                className="ml-auto text-gray-400 hover:text-red-600 p-1"
                title="Remove condition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addLeaf}
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
      >
        <Plus className="w-3.5 h-3.5" /> Add condition
      </button>
    </div>
  );
}
